import { z } from "zod";
import { TIPOS_ORGANIZACAO } from "./organizacao.js";
/**
 * Situacao funcional — o que a folha declara sobre a pessoa na competencia.
 *
 * `CEDIDO` e `DESLIGADO` entraram em 16/09/2026: a carga ja' declarava `CE` e
 * `DE`, mas o enum nao tinha onde guardar e a coluna gravava "desligado" como
 * `AFASTADO`. Os valores antigos (`FERIAS`, `AFASTADO`, `LICENCA_REMUNERADA`)
 * ficam por compatibilidade com base existente; a carga nao os produz.
 *
 * O valor de "ativo" e' `ATIVO`, o mesmo `AT` do layout. Ate' 16/09/2026 a base
 * gravava `TRABALHANDO`; a API o traduz na leitura e o rotulo ainda o conhece,
 * para o que foi gravado antes. Ver `SITUACAO_FUNCIONAL_ROTULOS`.
 */
export const SituacaoFuncionalSchema = z.enum([
    "ATIVO",
    "FERIAS",
    "AFASTADO",
    "LICENCA",
    "LICENCA_REMUNERADA",
    "CEDIDO",
    "DESLIGADO",
    "APOSENTADO",
]);
/** Rotulo para a tela, cobrindo tambem o apelido antigo `TRABALHANDO` (base gravada antes de 16/09/2026). */
export const SITUACAO_FUNCIONAL_ROTULOS = {
    ATIVO: "Ativo",
    TRABALHANDO: "Ativo",
    FERIAS: "Férias",
    AFASTADO: "Afastado",
    LICENCA: "Licença",
    LICENCA_REMUNERADA: "Licença remunerada",
    CEDIDO: "Cedido",
    DESLIGADO: "Desligado",
    APOSENTADO: "Aposentado",
};
/** Rotulo de qualquer valor que a base guarde; desconhecido volta como veio. */
export function rotuloSituacaoFuncional(valor) {
    const chave = (valor ?? "").trim().toUpperCase();
    return SITUACAO_FUNCIONAL_ROTULOS[chave] ?? (valor ?? "");
}
export const VinculoSchema = z.enum(["CLT", "ESTATUTARIO", "COMISSIONADO"]);
export const ServidorStatusSchema = z.enum(["ativo", "bloqueado", "arquivado"]);
/**
 * A organizacao do servidor, do jeito que a tela dele precisa (feature 009).
 *
 * `nome` e' o de EXIBICAO — "Prefeitura de Morada Nova" para uma prefeitura, a
 * sigla ("A.M.T") para o resto. A regra vive em `nomeExibicao`, no dominio, e
 * a API a aplica: o cliente NAO deve recompo-la a partir de `nome` + `tipo`,
 * porque duas copias da regra foi como "Prefeitura de" acabou colado na frente
 * de quem nao e' prefeitura.
 */
export const OrganizacaoDoServidorSchema = z.object({
    nome: z.string(),
    sigla: z.string().nullable(),
    tipo: z.enum(TIPOS_ORGANIZACAO),
    /** Rotulo em portugues do tipo, pronto para a tela ("Camara Municipal"). */
    rotuloTipo: z.string(),
});
export const ServidorSchema = z.object({
    id: z.number().int(),
    nome: z.string(),
    cpf_masked: z.string(),
    matricula: z.string(),
    prefeitura_id: z.number().int(),
    /**
     * OPCIONAL de proposito, e nao por falta de dado: a API pode nao resolver a
     * organizacao (servidor orfao), e um app novo pode falar com uma API
     * anterior a este campo. Quem le decide o rotulo generico do fallback.
     */
    organizacao: OrganizacaoDoServidorSchema.optional(),
    vinculo: VinculoSchema,
    situacao_funcional: SituacaoFuncionalSchema,
    status: ServidorStatusSchema,
});
export const TipoMargemSchema = z.enum(["EMPRESTIMO", "CARTAO_CONSIGNADO", "CARTAO_BENEFICIOS"]);
export const MargemSchema = z.object({
    salario_base: z.number(),
    comprometido: z.number(),
    disponivel: z.number(),
    percentual_uso: z.number(),
});
export const MargemPorTipoSchema = z.object({
    tipo: TipoMargemSchema,
    disponivel: z.number(),
    total: z.number(),
});
export const FonteSchema = z.object({
    tipo: z.string(),
    sincronizado_em: z.string().datetime(),
    cache_status: z.enum(["HIT", "MISS", "STALE"]),
});
export const MargemResponseSchema = z.object({
    servidor_id: z.number().int(),
    matricula: z.string(),
    prefeitura_id: z.number().int(),
    margem: MargemSchema,
    margens_por_tipo: z.array(MargemPorTipoSchema),
    fonte: FonteSchema,
    contratos: z.array(z.unknown()).optional(),
    _meta: z.object({ trace_id: z.string(), duracao_ms: z.number() }).optional(),
});
//# sourceMappingURL=servidor.js.map